/**
 * Occupational Therapy Discharge (Non-Visit) — options + empty payload.
 * After shared common header (1–6): Discharge Info → CMS checklist →
 * Homebound → Goals table → Functional status → Clinical summary →
 * Continued care → Instructions → Meds/Equipment (per OT Regular
 * Discharge Non-Visit screenshot). Therapist identity lives on the
 * identity strip; agency/patient live in the common header.
 */

import { emptyOtCommonHeader } from "./otNotesCommonHeaderUtils";

export const DISCHARGE_TYPE_OPTIONS = [
  "Regular (Planned)",
  "Non-Visit",
  "Other",
];

/** Row-major order so a 2-column grid matches the screenshot columns. */
export const DISCHARGE_REASON_OPTIONS = [
  "Goals Met",
  "Transfer to Another Agency",
  "Maximum Potential Achieved",
  "Moved Out of Service Area",
  "Patient Request",
  "No Longer Homebound",
];

export const CMS_CHECKLIST_OPTIONS = [
  "Reason for Discharge documented",
  "Goals/Outcomes addressed",
  "Functional Status documented",
  "Achieved vs. Not Achieved documented",
  "Patient/Caregiver Instructions provided",
  "Safety Considerations documented",
  "Plan for Continued Care documented",
  "Patient/Caregiver notified of discharge",
  "Documentation complete",
];

export const YES_NO_UNKNOWN_OPTIONS = ["Yes", "No", "Unknown"];

export const HOMEBOUND_NO_REASON_OPTIONS = [
  "Able to leave home without considerable and taxing effort",
  "Absences are not infrequent or of relatively short duration",
  "Patient resides in a facility / not in the home",
  "Other",
];

export const GOAL_OUTCOME_ROWS = [
  {
    key: "adlIadl",
    label: "ADL/IADL",
    defaultDescription: "Activities of daily living improvement",
  },
  {
    key: "functionalMobility",
    label: "Functional Mobility",
    defaultDescription: "Improve mobility and transfers",
  },
  {
    key: "homeSafety",
    label: "Home Safety",
    defaultDescription: "Home safety and fall prevention",
  },
  {
    key: "energyConservation",
    label: "Energy Conservation",
    defaultDescription: "Energy conservation techniques",
  },
  {
    key: "other",
    label: "Other (Specify)",
    defaultDescription: "",
  },
];

export const GOAL_OUTCOME_STATUS_OPTIONS = [
  "Achieved",
  "Partially Achieved",
  "Not Achieved",
  "Unable to assess",
];

export const ASSIST_STATUS_OPTIONS = [
  "Independent",
  "Modified Independent",
  "Supervision",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Dependent",
  "Unable to assess",
];

export const COGNITIVE_STATUS_OPTIONS = [
  "Intact",
  "Impaired",
  "Unable to assess",
];

export const SAFETY_AWARENESS_OPTIONS = [
  "Intact",
  "Impaired",
  "Unable to assess",
];

export const OVERALL_PROGRESS_OPTIONS = [
  "Improved",
  "Unchanged",
  "Declined",
  "Unable to assess",
];

export const PAIN_LEVEL_OPTIONS = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
];

/** Row-major order so a 2-column grid matches the screenshot columns. */
export const AREAS_ADDRESSED_OPTIONS = [
  "ADL Training",
  "IADL Training",
  "Therapeutic Exercise",
  "Functional Mobility",
  "Transfer Training",
  "ROM / Stretching",
  "Energy Conservation",
  "Home Safety",
  "Adaptive Equipment",
  "Fall Prevention",
  "Cognitive Retraining",
  "Other",
];

export const EDUCATION_PROVIDED_OPTIONS = [
  "Home Exercise Program",
  "Safety Precautions",
  "Energy Conservation",
  "Medication Management",
  "Equipment Use",
  "Other",
];

export const YES_NO_OPTIONS = ["Yes", "No"];

export const RECOMMENDED_SERVICE_OPTIONS = [
  "Outpatient OT",
  "Home Health OT (another agency)",
  "Skilled Nursing Facility",
  "Hospice",
  "Physician follow-up only",
  "Other",
];

export const INSTRUCTIONS_TO_OPTIONS = [
  "Patient",
  "Caregiver",
  "Family",
  "Other",
];

export const EDUCATION_METHOD_OPTIONS = [
  "Verbal",
  "Written",
  "Demonstration",
  "Other",
];

export const UNDERSTANDING_OPTIONS = ["Yes", "No", "N/A"];

export const DME_STATUS_OPTIONS = [
  "No New Equipment",
  "Equipment Provided/Recommended",
];

export const OT_DNV_ARRAY_KEYS = [
  "dischargeReasons",
  "cmsChecklist",
  "areasAddressed",
  "educationProvided",
  "instructionsTo",
  "educationMethods",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyGoalOutcomeRow = (def) => ({
  key: def.key,
  label: def.label,
  description: def.defaultDescription,
  status: "",
  outcomePercent: "",
});

export const emptyGoalOutcomes = () =>
  GOAL_OUTCOME_ROWS.map((def) => emptyGoalOutcomeRow(def));

export const mergeGoalOutcomes = (loaded) => {
  const byKey = {};
  (Array.isArray(loaded) ? loaded : []).forEach((row) => {
    if (row?.key) byKey[row.key] = row;
  });
  return GOAL_OUTCOME_ROWS.map((def) => {
    const row = byKey[def.key] || {};
    const status = String(row.status || "");
    return {
      key: def.key,
      label: def.label,
      description: String(
        row.description != null ? row.description : def.defaultDescription,
      ),
      status: GOAL_OUTCOME_STATUS_OPTIONS.includes(status) ? status : "",
      outcomePercent: String(row.outcomePercent || ""),
    };
  });
};

/** Full editable OT Discharge (Non-Visit) payload (JSON on dedicated row). */
export const emptyOtDischargeNonVisitPayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  dischargeDate: "",
  socDate: "",
  commonHeader: emptyOtCommonHeader(),

  // —— 7. Discharge Information ——
  dischargeType: "Non-Visit",
  dischargeTypeOther: "",
  dischargeReasons: [],
  dischargeReasonOther: "",
  dischargeSummary: "",
  supervisingTherapist: "",

  // —— 8. CMS Discharge Requirements ——
  cmsChecklist: [],

  // —— 9. Homebound Status ——
  stillHomebound: "",
  homeboundNoReason: "",

  // —— 10. OT Goals & Outcomes ——
  goalOutcomes: emptyGoalOutcomes(),

  // —— 11. Current Functional Status ——
  adlStatus: "",
  mobilityStatus: "",
  cognitiveStatus: "",
  painLevel: null,
  safetyAwareness: "",
  overallProgress: "",
  functionalComments: "",

  // —— 12. Clinical Summary ——
  areasAddressed: [],
  areasAddressedOther: "",
  interventionsBrief: "",
  progressSummary: "",
  educationProvided: [],
  educationOther: "",

  // —— 13. Continued Care Plan ——
  continuedTherapy: "",
  recommendedService: "",
  recommendedProvider: "",
  referralNeeded: "",
  followUpNotes: "",

  // —— 14. Discharge Instructions ——
  instructionsTo: [],
  educationMethods: [],
  understandingConfirmed: "",
  instructionNotes: "",

  // —— 15. Medication & Equipment ——
  medicationChanges: "",
  medicationChangeDetails: "",
  dmeStatus: "",
  equipmentDetails: "",
});
